<template>
  <v-container fluid>
    <div class="d-flex align-center mb-4">
      <h1 class="text-h4">
        Administrator Permissions
      </h1>
    </div>

    <p v-if="initialized === false"
       class="text-center">
      <v-progress-circular indeterminate
                           size="64">
      </v-progress-circular>
    </p>
    <template v-else>
      <p>
        Use this grid to make bulk changes to administrators' permissions.
      </p>
      <v-form @submit.prevent="submit">
        <v-card class="mb-4">
          <v-data-table id="data-table"
                        v-resize="adjustTableHeight"
                        :headers="headers"
                        :items="items"
                        :items-per-page="100"
                        :footer-props="{'items-per-page-options': [50, 100, 200]}"
                        :fixed-header="fixedHeader"
                        :height="tableHeight"
                        dense>
            <template v-slot:item="{index, item: user}">
              <tr>
                <td class="NameCell">
                  <div>
                    <a :href="`/admin/users/${user.id}/edit`"
                       target="_blank">
                      {{ user.name }}
                    </a>
                  </div>
                  <div>
                    <a href="#"
                       @click.prevent="grantAllPermissions(user)"
                       class="text-caption mr-2">
                      Grant all
                    </a>
                    <a href="#"
                       @click.prevent="revokeAllPermissions(user)"
                       class="text-caption">
                      Revoke all
                    </a>
                  </div>
                </td>
                <td v-for="permission in user.permissions"
                    :key="permission.key"
                    class="text-center">
                  <v-simple-checkbox :value="getPermissionValue(user, permission.key)"
                                     :disabled="user.id === currentUser.id && permission.key === 'can_edit_users'"
                                     @click.prevent="togglePermission(user, permission.key)">
                  </v-simple-checkbox>
                </td>
                <td class="NameCell">
                  <div>
                    <a :href="`/admin/users/${user.id}/edit`"
                       target="_blank">
                      {{ user.name }}
                    </a>
                  </div>
                  <div>
                    <a href="#"
                       @click.prevent="grantAllPermissions(user)"
                       class="text-caption mr-2">
                      Grant all
                    </a>
                    <a href="#"
                       @click.prevent="revokeAllPermissions(user)"
                       class="text-caption">
                      Revoke all
                    </a>
                  </div>
                </td>
              </tr>
            </template>
          </v-data-table>

          <v-card-actions>
            <v-btn type="submit"
                   color="primary"
                   :disabled="form.busy">
              <template v-if="form.busy">
                <v-icon left>
                  fal fa-spin fa-spinner
                </v-icon>
                Updating
              </template>
              <template v-else>
                Update Permissions
              </template>
            </v-btn>
          </v-card-actions>
        </v-card>
      </v-form>

      <v-card v-if="!form.busy && updated.length > 0"
              class="mb-4">
        <v-card-title class="pb-0">
          Updated {{ updated.length | pluralize('Administrator', false) }}:
        </v-card-title>
        <v-list class="pb-0">
          <v-divider></v-divider>
          <template v-for="object in updated">
            <v-list-item :key="object.id">
              <v-list-item-content>
                <v-list-item-title>
                  {{ object.name }}
                </v-list-item-title>
              </v-list-item-content>
            </v-list-item>
            <v-divider></v-divider>
          </template>
        </v-list>
      </v-card>
    </template>
  </v-container>
</template>
<script>
import {mapGetters} from 'vuex';
import {find, isBoolean, keys, orderBy} from 'lodash';
import userPermissionService from '../../services/userPermissionService';
import {Form} from 'vform';

export default {
  data() {
    return {
      administrators: [],
      form: new Form({
        permissions: [],
      }),
      updated: [],
      initialized: false,
      items: [],
      tableHeight: '600px',
    }
  },
  computed: {
    ...mapGetters([
      'currentUser',
      'loadingUsers',
      'team',
    ]),
    isLoading() {
      return this.loadingUsers;
    },
    activeAdministrators() {
      return this.administrators;
    },
    permissionKeys() {
      return orderBy(keys(userPermissionService.getDefaultPermissions()).filter(key => key.startsWith('can_')));
    },
    fixedHeader() {
      if (this.initialized === false) {
        return false;
      }
      return this.items.length > 50;
    },
    headers() {
      if (this.initialized === false) {
        return [];
      }
      const headers = [];
      headers.push({
        text: '',
        value: 'name',
      })
      this.permissionKeys.forEach(key => {
        headers.push({
          text: this.transformKeyToPhrase(key),
          value: key,
          sortable: false,
        })
      })
      headers.push({
        text: '',
        value: 'name',
      })
      return headers;
    },
  },
  watch: {
    isLoading: function (newVal) {
      if (newVal === false) {
        this.fetchData();
      }
    }
  },
  created() {
    this.initializeItems();
  },
  methods: {
    adjustTableHeight() {
      if (this.fixedHeader === false) {
        this.tableHeight = null;
        return null;
      }
      let offsetTop = 250;
      const tbody = document.querySelector('#data-table tbody');
      const tfoot = document.querySelector('#data-table .v-data-footer');
      if (tbody && tfoot) {
        offsetTop = tbody.getBoundingClientRect() + tfoot.offsetHeight + 20;
      }
      this.tableHeight = `calc(100vh - ${offsetTop}px)`;
    },
    initializeItems() {
      if (this.isLoading === false) {
        console.log('initializeItems');
        const items = [];
        this.activeAdministrators.forEach(user => {
          const item = {
            id: user.id,
            name: user.name,
            permissions: [],
          }
          this.permissionKeys.forEach(key => {
            const permissionItem = {
              id: key,
              key: key,
              name: this.transformKeyToPhrase(key),
              checked: this.getPermissionValue(user, key),
            }
            item[key] = permissionItem;
            item.permissions.push(permissionItem);
          })
          items.push(item);
        })
        this.items = items;
        this.initialized = true;
        this.adjustTableHeight();
      }
    },
    async fetchData() {
      this.form.busy = true;
      return Promise.all([
        axios.get(`/api/team/administrators`).then(response => {
          this.administrators = orderBy(response.data, ['slug'], ['asc']);
        }),
        axios.get(`/api/team/permissions`).then(response => {
          this.form.permissions = response.data;
        })
      ])
          .finally(() => {
            this.initializeItems();
            this.form.busy = false;
          })
    },
    transformKeyToPhrase(key) {
      return key
          // Split the string into an array of words
          .split('_')
          // Filter out the words you don't need, like 'can'
          .filter(word => word !== 'can')
          // Capitalize the first letter of each word
          .map(word => word.charAt(0).toUpperCase() + word.slice(1))
          // Join the words back into a string
          .join(' ');
    },
    getPermissionValue(user, key) {
      const perm = find(this.form.permissions, p => p.user_id === user.id)
      if (perm && perm[key] !== undefined && isBoolean(perm[key])) {
        return perm[key];
      }
      return false;
    },
    togglePermission(user, key) {
      if (this.form.busy) {
        return;
      }
      if (user.id === this.currentUser.id && key === 'can_edit_users') {
        return;
      }
      const perm = find(this.form.permissions, p => p.user_id === user.id)
      if (perm && perm[key] !== undefined && isBoolean(perm[key])) {
        perm[key] = !perm[key];
      }
    },
    grantAllPermissions(user) {
      if (this.form.busy) {
        return;
      }
      const userPermissions = find(this.form.permissions, p => p.user_id === user.id)
      if (userPermissions) {
        this.permissionKeys.forEach(key => {
          userPermissions[key] = true;
        })
      }
    },
    revokeAllPermissions(user) {
      if (this.form.busy) {
        return;
      }
      const userPermissions = find(this.form.permissions, p => p.user_id === user.id)
      if (userPermissions) {
        this.permissionKeys.forEach(key => {
          if (user.id === this.currentUser.id && key === 'can_edit_users') {

          } else {
            userPermissions[key] = false;
          }
        })
      }
    },
    submit() {
      if (this.form.busy) {
        return;
      }
      this.updated = [];
      return this.form.put(`/api/team/permissions`)
          .then(({data}) => {
            this.$store.dispatch('fetchUser');
            this.$toast.success('Permissions updated successfully.');
            this.updated = orderBy(data.updated, ['name'], ['asc']);
          })
          .catch(err => {
            this.$toast.error('Error updating permissions.');
          })
          .finally(() => {
            this.form.busy = false;
          })
    }
  }
}
</script>
